import React from 'react';
import { fmtNum, formatDateShort } from '@/lib/payroll';
import type { PayrollRow } from '@/lib/payroll';

type PrintMode = 'envelope' | 'a4' | 'payroll-summary' | 'stats-only';

// ✅ تحويل الساعات إلى أيام (1 يوم = 8 ساعات)
const HOURS_PER_DAY = 8;
const hoursToDays = (h: number) => (h / HOURS_PER_DAY).toFixed(2);

/* ───────── Envelope DL (110×220mm) — detailed ───────── */

export const EnvelopePage: React.FC<{
  employee: PayrollRow;
  period: { start: string | null; end: string | null };
  rawDays: any[];
  companyName: string;
}> = ({ employee, period, rawDays, companyName }) => {
  const totalOT = rawDays.reduce((s, d) => s + d.ot, 0);
  const totalLate = rawDays.reduce((s, d) => s + d.lateDed, 0);
  const totalEarly = rawDays.reduce((s, d) => s + (d.earlyDed || 0), 0);
  const totalMiss = rawDays.reduce((s, d) => s + d.missDed, 0);

  return (
    <div className="envelope-page">
      <div className="envelope-meta">
        <span>الفترة: {formatDateShort(period.start)} → {formatDateShort(period.end)}</span>
        <span>ID: {employee.ID}</span>
      </div>
      <div className="envelope-employee">
        <div className="envelope-to">{employee.Name}</div>
        <div className="envelope-job">{employee.Job} • {companyName}</div>
      </div>
      <table className="envelope-table">
        <thead>
          <tr>
            <th>البيان</th>
            <th>القيمة</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>يومية ({fmtNum(employee['Salary / Day'])} ج)</td><td>{employee['أيام الحضور المستحقة']} يوم</td></tr>
          <tr><td>أجر الحضور</td><td>{fmtNum(employee['أجر الحضور الأساسي'])}</td></tr>
          <tr><td>إضافي ({hoursToDays(employee['إجمالي ساعات الإضافي'])} يوم × {fmtNum(employee['Rate / Hour'] * HOURS_PER_DAY)} ج/يوم)</td><td>{fmtNum(employee['أجر الساعات الإضافية'])}</td></tr>
          {totalLate > 0 && <tr><td className="deduction">خصم تأخير</td><td className="deduction">-{fmtNum(employee['قيمة خصم التأخير'])}</td></tr>}
          {totalEarly > 0 && <tr><td className="deduction">خصم خروج مبكر</td><td className="deduction">-{fmtNum(employee['قيمة خصم الخروج المبكر'] || 0)}</td></tr>}
          {totalMiss > 0 && <tr><td className="deduction">خصم نسيان</td><td className="deduction">-{fmtNum(employee['قيمة خصم النسيان'])}</td></tr>}
          {/* ✅ التعديلات الإدارية — إضافة (+) أو خصم (-) حسب الإشارة (لو مش بصفر) */}
          {(employee['قيمة التعديلات الإدارية'] || 0) !== 0 && (
            <tr>
              <td className="addition">تعديل إداري</td>
              <td className="addition">
                {(employee['قيمة التعديلات الإدارية'] || 0) > 0
                  ? `+${fmtNum(employee['قيمة التعديلات الإدارية'])}`
                  : `-${fmtNum(Math.abs(employee['قيمة التعديلات الإدارية'] || 0))}`}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="envelope-amount-box">
        <div className="envelope-amount-label">صافي القبض المستحق</div>
        <div className="envelope-amount-value">{fmtNum(employee['صافي القبض المستحق'])} ج.م</div>
      </div>
    </div>
  );
};

/* ───────── Envelope DL — summary (multi-employee on one envelope, for long periods) ───────── */

export const SummaryPage: React.FC<{
  employee: PayrollRow;
  period: { start: string | null; end: string | null };
  rawDays: any[];
  allEmployees: PayrollRow[];
  missDeductions: { miss_in: number; miss_out: number };
  companyName: string;
}> = ({ employee, period, rawDays, allEmployees, companyName }) => {
  return (
    <div className="envelope-page">
      <div className="envelope-meta">
        <span>📅 {formatDateShort(period.start)} → {formatDateShort(period.end)}</span>
        <span>{companyName}</span>
      </div>
      <div className="envelope-employee">
        <div className="envelope-to">{employee.Name}</div>
        <div className="envelope-job">{employee.Job} • ID: {employee.ID}</div>
      </div>
      <table className="envelope-table">
        <thead>
          <tr>
            <th>الموظف</th>
            <th>أيام</th>
            <th>إضافي</th>
            <th>الصافي</th>
          </tr>
        </thead>
        <tbody>
          {allEmployees.map((e) => (
            <tr key={e.ID}>
              <td>{e.Name}</td>
              <td style={{ textAlign: 'center' }}>{e['أيام الحضور المستحقة']}</td>
              <td style={{ textAlign: 'center' }}>{e['إجمالي ساعات الإضافي']}س</td>
              <td>{fmtNum(e['صافي القبض المستحق'])}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="envelope-amount-box">
        <div className="envelope-amount-label">إجمالي الصافي — {allEmployees.length} موظف</div>
        <div className="envelope-amount-value">
          {fmtNum(allEmployees.reduce((s, e) => s + e['صافي القبض المستحق'], 0))} ج.م
        </div>
      </div>
    </div>
  );
};

/* ───────── A4 — Detailed per-employee ───────── */

export const A4Page: React.FC<{
  employee: PayrollRow;
  period: { start: string | null; end: string | null };
  rawDays: any[];
  companyName: string;
}> = ({ employee, period, rawDays, companyName }) => {
  const totalOT = rawDays.reduce((s, d) => s + d.ot, 0);
  const totalLate = rawDays.reduce((s, d) => s + d.lateDed, 0);
  const totalEarly = rawDays.reduce((s, d) => s + (d.earlyDed || 0), 0);
  const totalMiss = rawDays.reduce((s, d) => s + d.missDed, 0);
  const presentDays = rawDays.filter((d) => d.isPresent).length;
  const absentDays = rawDays.filter((d) => d.isAbsent).length;

  return (
    <div className="a4-page">
      <div className="a4-header">
        <div className="a4-company-name">💰 {companyName} — كشف مرتب موظف</div>
        <div className="a4-report-title">
          الفترة: من {formatDateShort(period.start)} إلى {formatDateShort(period.end)}
        </div>
      </div>
      <div className="a4-employee-info">
        <div className="a4-info-item">
          <div className="a4-info-label">الاسم</div>
          <div className="a4-info-value">{employee.Name}</div>
        </div>
        <div className="a4-info-item">
          <div className="a4-info-label">الرقم</div>
          <div className="a4-info-value">{employee.ID}</div>
        </div>
        <div className="a4-info-item">
          <div className="a4-info-label">المهنة</div>
          <div className="a4-info-value">{employee.Job}</div>
        </div>
      </div>
      <div className="a4-stats">
        <div className="a4-stat"><div className="a4-stat-label">أيام حضور</div><div className="a4-stat-value">{presentDays}</div></div>
        <div className="a4-stat"><div className="a4-stat-label">أيام غياب</div><div className="a4-stat-value">{absentDays}</div></div>
        <div className="a4-stat"><div className="a4-stat-label">إضافي (يوم)</div><div className="a4-stat-value">{hoursToDays(totalOT)}</div></div>
        <div className="a4-stat"><div className="a4-stat-label">جزاء تأخير (يوم)</div><div className="a4-stat-value">{totalLate.toFixed(2)}</div></div>
        <div className="a4-stat"><div className="a4-stat-label">جزاء خروج (يوم)</div><div className="a4-stat-value">{totalEarly.toFixed(2)}</div></div>
      </div>
      <div className="a4-section-title">تفاصيل البصمات</div>
      <table className="a4-table">
        <thead>
          <tr>
            <th style={{ width: '12%' }}>التاريخ</th>
            <th style={{ width: '11%' }}>دخول</th>
            <th style={{ width: '11%' }}>خروج</th>
            <th style={{ width: '9%' }}>حالة</th>
            <th style={{ width: '8%' }}>تأخير<br/>(د)</th>
            <th style={{ width: '8%' }}>مبكر<br/>(د)</th>
            <th style={{ width: '9%' }}>إضافي<br/>(س)</th>
            <th style={{ width: '10%' }}>جزاء</th>
          </tr>
        </thead>
        <tbody>
          {rawDays.map((d: any, i: number) => (
            <tr key={i}>
              <td>{formatDateShort(d.date)}</td>
              <td style={{ textAlign: 'center' }}>{d.clockIn === '--' ? '—' : d.clockIn}</td>
              <td style={{ textAlign: 'center' }}>{d.clockOut === '--' ? '—' : d.clockOut}</td>
              <td style={{ textAlign: 'center' }}>
                {d.isAbsent ? 'غياب' : d.status === 'Present' ? 'حضور' : d.status || '—'}
              </td>
              <td style={{ textAlign: 'center' }}>{d.lateDiffMins || '—'}</td>
              <td style={{ textAlign: 'center' }}>{d.earlyDiffMins || '—'}</td>
              <td style={{ textAlign: 'center' }}>{d.ot || '—'}</td>
              <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                {(d.lateDed + (d.earlyDed || 0) + d.missDed).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="a4-section-title">التفاصيل المالية</div>
      <div className="a4-split">
        <div>
          <table className="a4-table a4-summary-table">
            <tbody>
              <tr><td>يومية ({fmtNum(employee['Salary / Day'])} ج × {employee['أيام الحضور المستحقة']} يوم)</td><td style={{textAlign:'left', fontWeight:'bold'}}>{fmtNum(employee['أجر الحضور الأساسي'])}</td></tr>
              <tr><td>إضافي ({hoursToDays(employee['إجمالي ساعات الإضافي'])} يوم × {fmtNum(employee['Rate / Hour'] * HOURS_PER_DAY)} ج/يوم)</td><td style={{textAlign:'left', fontWeight:'bold'}}>{fmtNum(employee['أجر الساعات الإضافية'])}</td></tr>
              {/* ✅ التعديلات الإدارية — إضافة (+) أو خصم (-) حسب الإشارة */}
              {(employee['قيمة التعديلات الإدارية'] || 0) !== 0 && (
                <tr>
                  <td>تعديلات إدارية</td>
                  <td style={{
                    textAlign: 'left',
                    fontWeight: 'bold',
                    color: (employee['قيمة التعديلات الإدارية'] || 0) > 0 ? '#1e40af' : '#dc2626',
                  }}>
                    {(employee['قيمة التعديلات الإدارية'] || 0) > 0
                      ? `+${fmtNum(employee['قيمة التعديلات الإدارية'])}`
                      : `-${fmtNum(Math.abs(employee['قيمة التعديلات الإدارية'] || 0))}`}
                  </td>
                </tr>
              )}
              <tr><td style={{color:'#dc2626'}}>خصم تأخير</td><td style={{textAlign:'left', fontWeight:'bold', color:'#dc2626'}}>{employee['قيمة خصم التأخير'] > 0 ? `-${fmtNum(employee['قيمة خصم التأخير'])}` : '—'}</td></tr>
              <tr><td style={{color:'#dc2626'}}>خصم خروج مبكر</td><td style={{textAlign:'left', fontWeight:'bold', color:'#dc2626'}}>{(employee['قيمة خصم الخروج المبكر'] || 0) > 0 ? `-${fmtNum(employee['قيمة خصم الخروج المبكر'] || 0)}` : '—'}</td></tr>
              <tr><td style={{color:'#dc2626'}}>خصم نسيان</td><td style={{textAlign:'left', fontWeight:'bold', color:'#dc2626'}}>{employee['قيمة خصم النسيان'] > 0 ? `-${fmtNum(employee['قيمة خصم النسيان'])}` : '—'}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="a4-net-box">
          <div className="a4-net-label">صافي القبض المستحق</div>
          <div className="a4-net-value">{fmtNum(employee['صافي القبض المستحق'])} ج.م</div>
          <div style={{ fontSize: '9pt', marginTop: '2mm', opacity: 0.9 }}>
            إجمالي {fmtNum(employee['أجر الحضور الأساسي'] + employee['أجر الساعات الإضافية'] + (employee['قيمة التعديلات الإدارية'] || 0) - employee['قيمة خصم التأخير'] - (employee['قيمة خصم الخروج المبكر'] || 0) - employee['قيمة خصم النسيان'])} ج.م
          </div>
        </div>
      </div>

      <div className="a4-signatures">
        <div className="a4-signature-block">
          <div><strong>معــد الكشف</strong></div>
          <div className="a4-signature-line">التوقيع والتاريخ</div>
        </div>
        <div className="a4-signature-block">
          <div><strong>المحاسب</strong></div>
          <div className="a4-signature-line">التوقيع والتاريخ</div>
        </div>
        <div className="a4-signature-block">
          <div><strong>المدير المالي</strong></div>
          <div className="a4-signature-line">التوقيع والتاريخ</div>
        </div>
      </div>
    </div>
  );
};

/* ───────── A4 Landscape — Payroll Summary Report ───────── */

export const PayrollSummaryReport: React.FC<{
  employees: PayrollRow[];
  period: { start: string | null; end: string | null };
  adminAdj: Record<string, number>;
  companyName: string;
}> = ({ employees, period, adminAdj, companyName }) => {
  const totalNet = employees.reduce((s, e) => s + e['صافي القبض المستحق'], 0);
  const totalBase = employees.reduce((s, e) => s + e['أجر الحضور الأساسي'], 0);
  const totalOT = employees.reduce((s, e) => s + e['أجر الساعات الإضافية'], 0);
  const totalLate = employees.reduce((s, e) => s + e['قيمة خصم التأخير'], 0);
  const totalEarly = employees.reduce((s, e) => s + (e['قيمة خصم الخروج المبكر'] || 0), 0);
  const totalMiss = employees.reduce((s, e) => s + e['قيمة خصم النسيان'], 0);
  const totalAdj = employees.reduce((s, e) => s + (e['قيمة التعديلات الإدارية'] || 0), 0);
  const totalDays = employees.reduce((s, e) => s + e['أيام الحضور المستحقة'], 0);
  const totalOTHours = employees.reduce((s, e) => s + e['إجمالي ساعات الإضافي'], 0);
  const totalDed = totalLate + totalEarly + totalMiss;
  const totalLateMins = employees.reduce((s, e) => s + (e['دقائق التأخير'] || 0), 0);
  const totalEarlyMins = employees.reduce((s, e) => s + (e['دقائق الخروج المبكر'] || 0), 0);
  const issueDate = new Date().toLocaleDateString('ar-EG', { numberingSystem: 'latn' });

  return (
    <div className="general-page">
      <div className="general-header">
        <div className="a4-company-name">💰 {companyName} — تقرير المرتبات المجمع</div>
        <div className="general-period">
          الفترة: من {formatDateShort(period.start)} إلى {formatDateShort(period.end)} • عدد الموظفين:{' '}
          {employees.length} • تاريخ الإصدار: {issueDate}
        </div>
      </div>
      <div className="general-summary">
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الصافي</div><div className="general-sum-value" style={{ color: '#059669' }}>{fmtNum(totalNet)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الأساسي</div><div className="general-sum-value">{fmtNum(totalBase)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الإضافي</div><div className="general-sum-value">{fmtNum(totalOT)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الخصومات</div><div className="general-sum-value" style={{ color: '#dc2626' }}>-{fmtNum(totalDed)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي التعديلات</div><div className="general-sum-value" style={{ color: '#1e40af' }}>{fmtNum(totalAdj)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الأيام</div><div className="general-sum-value">{totalDays} يوم</div></div>
      </div>
      <div className="a4-section-title">📊 تفاصيل المرتبات لكل موظف</div>
      <table className="general-table">
        <thead>
          <tr>
            <th style={{ width: '2%' }}>#</th>
            <th style={{ width: '3%' }}>ID</th>
            <th style={{ width: '13%' }}>الاسم</th>
            <th style={{ width: '8%' }}>الوظيفة</th>
            <th style={{ width: '5%' }}>يومية</th>
            <th style={{ width: '4%' }}>أيام</th>
            <th style={{ width: '5%' }}>إضافي<br/>(س)</th>
            <th style={{ width: '4%' }}>د.ت</th>
            <th style={{ width: '6%' }}>خصم تأخير</th>
            <th style={{ width: '4%' }}>د.خ</th>
            <th style={{ width: '6%' }}>خصم خ.مب</th>
            <th style={{ width: '6%' }}>خصم نسيان</th>
            <th style={{ width: '5%' }}>إجمالي خصم</th>
            <th style={{ width: '4%' }}>تعديل<br/>(يوم)</th>
            <th style={{ width: '5%' }}>قيمة<br/>التعديل</th>
            <th style={{ width: '9%' }}>الصافي</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((e, i) => {
            const adjDays = Number(adminAdj?.[e.ID] || 0);
            const adjVal = adjDays * e['Salary / Day'];
            const empDed = e['قيمة خصم التأخير'] + (e['قيمة خصم الخروج المبكر'] || 0) + e['قيمة خصم النسيان'];
            // ✅ صافي القبض المستحق بالفعل شامل adminVal — مفيش داعي نضيفه تاني
            const empTotalNet = e['صافي القبض المستحق'];
            return (
              <tr key={e.ID}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td style={{ textAlign: 'center' }}>{e.ID}</td>
                <td><strong>{e.Name}</strong></td>
                <td style={{ fontSize: '8pt', color: '#475569' }}>{e.Job}</td>
                <td style={{ textAlign: 'center' }}>{fmtNum(e['Salary / Day'])}</td>
                <td style={{ textAlign: 'center' }}>{e['أيام الحضور المستحقة']}</td>
                <td style={{ textAlign: 'center' }}>{e['إجمالي ساعات الإضافي']}</td>
                <td style={{ textAlign: 'center', fontSize: '8pt', color: e['دقائق التأخير'] > 0 ? '#dc2626' : 'inherit' }}>{e['دقائق التأخير'] || '-'}</td>
<td style={{ textAlign: 'left', color: e['قيمة خصم التأخير'] > 0 ? '#dc2626' : 'inherit' }}>{e['قيمة خصم التأخير'] > 0 ? `-${fmtNum(e['قيمة خصم التأخير'])}` : '-'}</td>
                <td style={{ textAlign: 'center', fontSize: '8pt', color: (e['دقائق الخروج المبكر'] || 0) > 0 ? '#dc2626' : 'inherit' }}>{(e['دقائق الخروج المبكر'] || 0) || '-'}</td>
                <td style={{ textAlign: 'left', color: (e['قيمة خصم الخروج المبكر'] || 0) > 0 ? '#dc2626' : 'inherit' }}>{(e['قيمة خصم الخروج المبكر'] || 0) > 0 ? `-${fmtNum(e['قيمة خصم الخروج المبكر'] || 0)}` : '-'}</td>
                <td style={{ textAlign: 'left', color: e['قيمة خصم النسيان'] > 0 ? '#dc2626' : 'inherit' }}>{e['قيمة خصم النسيان'] > 0 ? `-${fmtNum(e['قيمة خصم النسيان'])}` : '-'}</td>
                <td style={{ textAlign: 'left', color: empDed > 0 ? '#dc2626' : 'inherit', fontWeight: 'bold' }}>{empDed > 0 ? `-${fmtNum(empDed)}` : '-'}</td>
                <td style={{ textAlign: 'center', color: adjDays !== 0 ? '#1e40af' : 'inherit', fontWeight: 'bold' }}>{adjDays !== 0 ? adjDays.toFixed(2) : '-'}</td>
                <td style={{ textAlign: 'left', color: adjVal !== 0 ? '#1e40af' : 'inherit', fontWeight: 'bold' }}>{adjVal !== 0 ? fmtNum(adjVal) : '-'}</td>
                <td style={{ textAlign: 'left', fontWeight: 'bold', color: '#059669', fontSize: '9pt', whiteSpace: 'nowrap' }}>{fmtNum(empTotalNet)}</td>
              </tr>
            );
          })}
          <tr style={{ background: '#1e40af', color: 'white', fontWeight: 'bold' }}>
            <td colSpan={4} style={{ textAlign: 'center', padding: '2mm' }}>الإجمالي</td>
            <td style={{ textAlign: 'center' }}>—</td>
<td style={{ textAlign: 'center' }}>{totalDays}</td>
            <td style={{ textAlign: 'center' }}>{totalOTHours}</td>
            <td style={{ textAlign: 'center', color: '#fca5a5' }}>{totalLateMins}</td>
            <td style={{ textAlign: 'left', color: '#fca5a5' }}>-{fmtNum(totalLate)}</td>
            <td style={{ textAlign: 'center', color: '#fca5a5' }}>{totalEarlyMins}</td>
            <td style={{ textAlign: 'left', color: '#fca5a5' }}>-{fmtNum(totalEarly)}</td>
            <td style={{ textAlign: 'left', color: '#fca5a5' }}>-{fmtNum(totalMiss)}</td>
            <td style={{ textAlign: 'left', color: '#fca5a5', fontWeight: 'bold' }}>-{fmtNum(totalDed)}</td>
            <td style={{ textAlign: 'center', color: '#86efac' }}>{Object.values(adminAdj || {}).reduce((s, v) => s + Number(v || 0), 0).toFixed(2)}</td>
            <td style={{ textAlign: 'left', color: '#86efac' }}>{fmtNum(totalAdj)}</td>
            {/* ✅ totalNet بالفعل شامل التعديلات — مفيش داعي نضيف totalAdj تاني */}
            <td style={{ textAlign: 'left', color: '#86efac', fontSize: '10pt', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{fmtNum(totalNet)}</td>
          </tr>
        </tbody>
      </table>
      <div className="a4-signatures" style={{ marginTop: '8mm', paddingTop: '5mm', borderTop: '2px solid #000' }}>
        <div className="a4-signature-block"><div><strong>معــد التقرير</strong></div><div className="a4-signature-line">التوقيع والتاريخ</div></div>
        <div className="a4-signature-block"><div><strong>المحاسب</strong></div><div className="a4-signature-line">التوقيع والتاريخ</div></div>
        <div className="a4-signature-block"><div><strong>المدير المالي</strong></div><div className="a4-signature-line">التوقيع والتاريخ</div></div>
      </div>
      <div className="general-footer">
        <div>تاريخ الإصدار: {issueDate}</div>
        <div><strong>للحسابات</strong> - يُرجى المراجعة والتوقيع</div>
      </div>
    </div>
  );
};

/* ───────── A4 Landscape — Results Stats Report (separate stats page) ───────── */

export const ResultsStatsReport: React.FC<{
  employees: PayrollRow[];
  period: { start: string | null; end: string | null };
  stats: any;
  companyName: string;
}> = ({ employees, period, stats, companyName }) => {
  const fmt = (n: number) => Number(n || 0).toLocaleString('en-US');
  const issueDate = new Date().toLocaleDateString('ar-EG', { numberingSystem: 'latn' });
  const byJob: Record<string, { count: number; total: number; net: number; ot: number; days: number; ded: number }> = {};
  employees.forEach((e) => {
    if (!byJob[e.Job]) byJob[e.Job] = { count: 0, total: 0, net: 0, ot: 0, days: 0, ded: 0 };
    byJob[e.Job].count += 1;
    byJob[e.Job].total += e['أجر الحضور الأساسي'] + e['أجر الساعات الإضافية'] + e['قيمة التعديلات الإدارية'];
    byJob[e.Job].net += e['صافي القبض المستحق'];
    byJob[e.Job].ot += e['إجمالي ساعات الإضافي'];
    byJob[e.Job].days += e['أيام الحضور المستحقة'];
    byJob[e.Job].ded += e['قيمة خصم التأخير'] + (e['قيمة خصم الخروج المبكر'] || 0) + e['قيمة خصم النسيان'];
  });
  return (
    <div className="general-page">
      <div className="general-header">
        <div className="a4-company-name">📊 {companyName} — إحصائيات المرتبات</div>
        <div className="general-period">
          الفترة: من {formatDateShort(period.start)} إلى {formatDateShort(period.end)} • عدد الموظفين:{' '}
          {employees.length} • تاريخ الإصدار: {issueDate}
        </div>
      </div>
      <div className="general-summary">
        <div className="general-sum-item"><div className="general-sum-label">إجمالي صافي المرتبات</div><div className="general-sum-value" style={{ color: '#059669' }}>{fmtNum(stats.totalNet)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الراتب</div><div className="general-sum-value">{fmtNum(stats.totalGross)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الإضافي</div><div className="general-sum-value" style={{ color: '#0369a1' }}>{fmtNum(stats.totalOT)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">إجمالي الخصومات</div><div className="general-sum-value" style={{ color: '#dc2626' }}>-{fmtNum(stats.totalDed)}</div></div>
        <div className="general-sum-item"><div className="general-sum-label">دقائق التأخير</div><div className="general-sum-value" style={{ color: '#d97706', fontSize: '11pt' }}>{stats.totalLateMins} د</div></div>
        <div className="general-sum-item"><div className="general-sum-label">دقائق الخروج المبكر</div><div className="general-sum-value" style={{ color: '#d97706', fontSize: '11pt' }}>{stats.totalEarlyMins} د</div></div>
      </div>
      <div className="a4-section-title">📈 تفصيل الإحصائيات</div>
      <table className="general-table">
        <thead>
          <tr>
            <th style={{ width: '30%' }}>المؤشر</th>
            <th style={{ width: '20%' }}>القيمة الإجمالية</th>
            <th style={{ width: '20%' }}>متوسط لكل موظف</th>
            <th style={{ width: '15%' }}>النسبة من الإجمالي</th>
            <th style={{ width: '15%' }}>ملاحظات</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>إجمالي أيام الحضور</strong></td>
            <td><strong style={{ color: '#1e40af' }}>{stats.presentDays} يوم</strong></td>
            <td>{(stats.presentDays / stats.employees).toFixed(2)} يوم/موظف</td>
            <td>100%</td>
            <td>أيام العمل الفعلية</td>
          </tr>
          <tr>
            <td><strong>إجمالي الإضافي</strong></td>
            <td><strong style={{ color: '#0369a1' }}>{hoursToDays(stats.otHours)} يوم</strong></td>
            <td>{hoursToDays(stats.otHours / stats.employees)} يوم/موظف</td>
            <td>{stats.totalGross > 0 ? ((stats.totalOT / stats.totalGross) * 100).toFixed(1) : 0}% من الإجمالي</td>
            <td>إضافي مقرب لـ 2/4/6/8 ساعة (1 يوم = 8 ساعات)</td>
          </tr>
          <tr>
            <td><strong>دقائق التأخير</strong></td>
            <td><strong style={{ color: '#d97706' }}>{stats.totalLateMins} دقيقة</strong></td>
            <td>{(stats.totalLateMins / stats.employees).toFixed(0)} د/موظف</td>
            <td>{(stats.totalLateMins / 60).toFixed(1)} ساعة</td>
            <td>مجموع الفروقات بالدقائق</td>
          </tr>
          <tr>
            <td><strong>دقائق الخروج المبكر</strong></td>
            <td><strong style={{ color: '#d97706' }}>{stats.totalEarlyMins} دقيقة</strong></td>
            <td>{(stats.totalEarlyMins / stats.employees).toFixed(0)} د/موظف</td>
            <td>{(stats.totalEarlyMins / 60).toFixed(1)} ساعة</td>
            <td>مجموع الفروقات بالدقائق</td>
          </tr>
          <tr>
            <td><strong>أيام جزاء التأخير</strong></td>
            <td><strong style={{ color: '#dc2626' }}>{stats.lateDays.toFixed(3)} يوم</strong></td>
            <td>{(stats.lateDays / stats.employees).toFixed(3)} يوم/موظف</td>
            <td>{fmtNum(stats.totalLateDed)} ج.م</td>
            <td>جزاء على التأخير</td>
          </tr>
          <tr>
            <td><strong>أيام جزاء الخروج المبكر</strong></td>
            <td><strong style={{ color: '#dc2626' }}>{stats.earlyDays.toFixed(3)} يوم</strong></td>
            <td>{(stats.earlyDays / stats.employees).toFixed(3)} يوم/موظف</td>
            <td>{fmtNum(stats.totalEarlyDed)} ج.م</td>
            <td>جزاء على الخروج المبكر</td>
          </tr>
          <tr>
            <td><strong>أيام جزاء النسيان</strong></td>
            <td><strong style={{ color: '#dc2626' }}>{stats.missDays.toFixed(3)} يوم</strong></td>
            <td>{(stats.missDays / stats.employees).toFixed(3)} يوم/موظف</td>
            <td>{fmtNum(stats.totalMissDed)} ج.م</td>
            <td>جزاء على نسيان البصمة</td>
          </tr>
          <tr>
            <td><strong>التعديلات الإدارية</strong></td>
            <td><strong style={{ color: '#1e40af' }}>{stats.adjDays.toFixed(2)} يوم</strong></td>
            <td>{(stats.adjDays / stats.employees).toFixed(2)} يوم/موظف</td>
            <td>{fmtNum(stats.totalAdj)} ج.م</td>
            <td>إضافات / خصومات إدارية</td>
          </tr>
        </tbody>
      </table>
      <div className="a4-section-title">📋 تقسيم حسب الوظيفة</div>
      <table className="general-table">
        <thead>
          <tr>
            <th style={{ width: '3%' }}>#</th>
            <th style={{ width: '20%' }}>الوظيفة</th>
            <th style={{ width: '8%' }}>عدد الموظفين</th>
            <th style={{ width: '10%' }}>إجمالي أيام</th>
            <th style={{ width: '11%' }}>إجمالي الإضافي (يوم)</th>
            <th style={{ width: '14%' }}>إجمالي الراتب</th>
            <th style={{ width: '14%' }}>إجمالي الخصومات</th>
            <th style={{ width: '20%' }}>إجمالي الصافي</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byJob).map(([job, data], idx) => (
            <tr key={job}>
              <td style={{ textAlign: 'center' }}>{idx + 1}</td>
              <td><strong>{job}</strong></td>
              <td style={{ textAlign: 'center' }}>{data.count}</td>
              <td style={{ textAlign: 'center' }}>{data.days}</td>
              <td style={{ textAlign: 'center' }}>{hoursToDays(data.ot)}</td>
              <td style={{ textAlign: 'left' }}>{fmtNum(data.total)}</td>
              <td style={{ textAlign: 'left', color: data.ded > 0 ? '#dc2626' : 'inherit' }}>{data.ded > 0 ? `-${fmtNum(data.ded)}` : '-'}</td>
              <td style={{ textAlign: 'left', fontWeight: 'bold', color: '#059669' }}>{fmtNum(data.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="general-footer">
        <div>تاريخ الإصدار: {issueDate}</div>
        <div><strong>للمراجعة والتدقيق</strong> - إحصائيات تفصيلية</div>
      </div>
    </div>
  );
};

export { type PrintMode };
